import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Toolbar,
  Skeleton,
  Alert,
  Snackbar,
  Chip,
  TablePagination,
  InputAdornment,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Fade,
  Zoom,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import CategoryIcon from "@mui/icons-material/Category";
import ArticleIcon from "@mui/icons-material/Article";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import SideBar from "../dashboard admin/components/SideBar";
import api from "../../../services/api/api";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const PublicationsAdmin = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [publications, setPublications] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  
  // Paginação
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Snackbar states
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Função para mostrar notificações
  const showNotification = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Função para buscar publicações do backend
  useEffect(() => {
    const fetchPublications = async () => {
      try {
        setLoading(true);
        const response = await api.get("/api/getAllPublications");
        setPublications(response.data);
        setError(null);
      } catch (err) {
        setError("Erro ao carregar publicações!");
        showNotification("Erro ao carregar publicações!", "error");
      } finally {
        setLoading(false);
      }
    };

    const fetchCategories = async () => {
      try {
        const response = await api.get("/api/getAllCategories");
        const categoriesMap = response.data.reduce((acc, category) => {
          acc[category._id] = category.name;
          return acc;
        }, {});
        setCategories(categoriesMap);
      } catch (err) {
        setError("Erro ao carregar todas as categorias!");
        showNotification("Erro ao carregar todas as categorias!", "error");
      }
    };

    fetchPublications();
    fetchCategories();
  }, []);

  // Handle search
  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(0); // Reset page when searching
  };

  // Filtered list
  const filteredPublications = publications.filter((item) =>
    item.name.toLowerCase().includes(search.toLowerCase()) ||
    item.category?.toLowerCase().includes(search.toLowerCase()) ||
    categories[item.category]?.toLowerCase().includes(search.toLowerCase())
  );

  // Paginação
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Função para redirecionar para a página de edição
  const handleEdit = (publicationId) => {
    navigate(`/admin/edipublication/${publicationId}`);
  };

  // Função para excluir a publicação com SweetAlert2
  const handleDeleteClick = async (publicationId, publicationTitle) => {
    const result = await Swal.fire({
      title: "Tem certeza?",
      text: `Deseja excluir a publicação "${publicationTitle}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Sim, excluir!",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
      backdrop: true,
      allowOutsideClick: false,
    });

    if (result.isConfirmed) {
      try {
        // Mostrar loading
        Swal.fire({
          title: "Excluindo...",
          text: "Por favor, aguarde",
          icon: "info",
          allowOutsideClick: false,
          showConfirmButton: false,
          willOpen: () => {
            Swal.showLoading();
          },
        });

        const token = localStorage.getItem("token");
        await api.delete(`/api/deletePublication/${publicationId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        // Atualiza o estado removendo a publicação excluída
        setPublications(publications.filter((item) => item.id !== publicationId));
        
        // Mostrar sucesso
        Swal.fire({
          title: "Excluída!",
          text: "A publicação foi excluída com sucesso.",
          icon: "success",
          timer: 3000,
          showConfirmButton: false,
        });

        showNotification("Publicação excluída com sucesso!", "success");
      } catch (err) {
        // Mostrar erro
        Swal.fire({
          title: "Erro!",
          text: "Ocorreu um erro ao excluir a publicação.",
          icon: "error",
          confirmButtonText: "OK",
        });
        showNotification("Erro ao excluir publicação!", "error");
      }
    }
  };

  // Skeleton para carregamento
  const renderSkeleton = () => (
    <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 3 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell><strong>Título</strong></TableCell>
            <TableCell><strong>Categoria</strong></TableCell>
            <TableCell><strong>Publicado</strong></TableCell>
            <TableCell><strong>Data</strong></TableCell>
            <TableCell align="right"><strong>Ações</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[...Array(rowsPerPage)].map((_, index) => (
            <TableRow key={index}>
              <TableCell><Skeleton animation="wave" /></TableCell>
              <TableCell><Skeleton animation="wave" /></TableCell>
              <TableCell><Skeleton animation="wave" /></TableCell>
              <TableCell><Skeleton animation="wave" /></TableCell>
              <TableCell align="right">
                <Skeleton animation="wave" width={80} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // Renderizar status de publicação
  const renderPublishStatus = (isPublished) => (
    <Chip
      icon={isPublished ? <CheckCircleIcon /> : <CancelIcon />}
      label={isPublished ? "Publicado" : "Rascunho"}
      color={isPublished ? "success" : "warning"}
      size="small"
      variant="outlined"
    />
  );

  // Estatísticas rápidas
  const totalPublications = publications.length;
  const publishedPublications = publications.filter(item => item.isPublished).length;
  const draftPublications = totalPublications - publishedPublications;

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
      {/* Sidebar */}
      <Box>
        <SideBar />
      </Box>

      {/* Main Content */}
      <Box sx={{ flex: 1, p: 3 }}>
        <Fade in={true} timeout={800}>
          <Box>
            <Typography variant="h4" fontWeight="bold" mb={3}>
              Administração de Publicações
            </Typography>

            {/* Estatísticas */}
            {!loading && (
              <Grid container spacing={3} mb={3}>
                <Grid item xs={12} sm={4}>
                  <Zoom in={true} timeout={500}>
                    <Card sx={{ 
                      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      color: "white"
                    }}>
                      <CardContent>
                        <Box display="flex" alignItems="center">
                          <MenuBookIcon sx={{ mr: 2, fontSize: 40 }} />
                          <Box>
                            <Typography variant="h4" fontWeight="bold">
                              {totalPublications}
                            </Typography>
                            <Typography variant="body2">
                              Total de Publicações
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Zoom>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Zoom in={true} timeout={700}>
                    <Card sx={{ 
                      background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                      color: "white"
                    }}>
                      <CardContent>
                        <Box display="flex" alignItems="center">
                          <CheckCircleIcon sx={{ mr: 2, fontSize: 40 }} />
                          <Box>
                            <Typography variant="h4" fontWeight="bold">
                              {publishedPublications}
                            </Typography>
                            <Typography variant="body2">
                              Publicadas
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Zoom>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Zoom in={true} timeout={900}>
                    <Card sx={{ 
                      background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                      color: "white"
                    }}>
                      <CardContent>
                        <Box display="flex" alignItems="center">
                          <CancelIcon sx={{ mr: 2, fontSize: 40 }} />
                          <Box>
                            <Typography variant="h4" fontWeight="bold">
                              {draftPublications}
                            </Typography>
                            <Typography variant="body2">
                              Rascunhos
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Zoom>
                </Grid>
              </Grid>
            )}

            {/* Alert de erro */}
            {error && (
              <Alert 
                severity="error" 
                sx={{ mb: 2 }}
                onClose={() => setError(null)}
              >
                {error}
              </Alert>
            )}

            {/* Toolbar */}
            <Paper 
              elevation={3}
              sx={{
                p: 2,
                mb: 3,
                borderRadius: 2,
                background: "linear-gradient(90deg, #ffffff 0%, #f8f9fa 100%)"
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                <TextField
                  size="small"
                  variant="outlined"
                  placeholder="Buscar publicações..."
                  value={search}
                  onChange={handleSearch}
                  sx={{ minWidth: 300 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />

                <Box display="flex" gap={2}>
                  <Tooltip title="Gerenciar Categorias">
                    <Button
                      href="categories"
                      variant="contained"
                      startIcon={<CategoryIcon />}
                      sx={{
                        background: "linear-gradient(90deg, #ff6b6b, #ee5a52)",
                        textTransform: "none",
                        fontWeight: "bold",
                        "&:hover": {
                          background: "linear-gradient(90deg, #ee5a52, #ff6b6b)",
                        }
                      }}
                    >
                      Categorias
                    </Button>
                  </Tooltip>

                  <Tooltip title="Criar Nova Publicação">
                    <Button
                      href="creatpubli"
                      variant="contained"
                      startIcon={<AddIcon />}
                      sx={{
                        background: "linear-gradient(90deg, #1cb5e0, #000851)",
                        textTransform: "none",
                        fontWeight: "bold",
                        "&:hover": {
                          background: "linear-gradient(90deg, #000851, #1cb5e0)",
                        }
                      }}
                    >
                      Nova Publicação
                    </Button>
                  </Tooltip>
                </Box>
              </Box>
            </Paper>

            {/* Tabela de Publicações */}
            {loading ? (
              renderSkeleton()
            ) : (
              <Paper elevation={3} sx={{ borderRadius: 2 }}>
                <TableContainer>
                  <Table>
                    <TableHead sx={{ backgroundColor: "#f8f9fa" }}>
                      <TableRow>
                        <TableCell><strong>Título</strong></TableCell>
                        <TableCell><strong>Categoria</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell><strong>Data</strong></TableCell>
                        <TableCell align="right"><strong>Ações</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredPublications
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((item, index) => (
                          <TableRow 
                            key={item.id}
                            sx={{ 
                              "&:hover": { backgroundColor: "#f5f5f5" },
                              animation: `fadeIn 0.5s ease-in-out ${index * 0.1}s both`
                            }}
                          >
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {item.name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={categories[item.category] || item.category || 'Categoria não encontrada'} 
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              {renderPublishStatus(item.isPublished)}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {item.publicationDate
                                  ? new Date(item.publicationDate).toLocaleDateString("pt-BR")
                                  : "Não disponível"}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Box display="flex" gap={1} justifyContent="flex-end">
                                <Tooltip title="Editar">
                                  <IconButton 
                                    color="primary" 
                                    onClick={() => handleEdit(item.id)}
                                    size="small"
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Excluir">
                                  <IconButton 
                                    color="error" 
                                    onClick={() => handleDeleteClick(item.id, item.name)}
                                    size="small"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {/* Paginação */}
                <TablePagination
                  component="div"
                  count={filteredPublications.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  labelRowsPerPage="Linhas por página:"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
                  }
                />
              </Paper>
            )}
          </Box>
        </Fade>
      </Box>

      {/* Snackbar para notificações */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* CSS para animações */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </Box>
  );
};

export default PublicationsAdmin;